/**
 * Service layer for the enrichment workflow controls. Thin typed wrappers over
 * `workflowRunsClient` (the workflow-webapp control service) + `endpoints`. All
 * calls are tenant-scoped by the JWT `subsidiaryId` claim; responses are bare
 * JSON (no `{ data: T }` envelope), matching the sibling control endpoints.
 *
 * The `{workflowId}` path segment IS the root workflow id for every control
 * endpoint below — the contract names it `runId` but documents it as the root
 * workflow id, which is what we hold at the stepper level.
 */
import { workflowRunsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';

import type {
  EnrichmentControlRequest,
  EnrichmentControlResponse,
  EnrichmentFailurePointsResponse,
  EnrichmentRunHealth,
  EnrichmentRunProgress,
  RestartEnrichmentRequest,
  RestartEnrichmentResponse,
  WorkflowRetryMode,
  WorkflowRewindResponse,
} from '../types/workflow-control.types';
import { deriveRestartProgress, type RestartRunDetail } from '../utils/derive-restart-progress';

// ---------------------------------------------------------------------------
// Retry — discover + rewind (#1 → #2)
// ---------------------------------------------------------------------------

/** #1 — list retryable Temporal failure points for a failed enrichment run.
 *  Each carries the `resetToEventId` the rewind (#2) must target. */
export async function getEnrichmentFailurePoints(
  workflowId: string,
): Promise<EnrichmentFailurePointsResponse> {
  return workflowRunsClient
    .get(endpoints.catalogOperationsWorkflow.failurePoints(workflowId))
    .json<EnrichmentFailurePointsResponse>();
}

/** #2 — rewind (`mode: 'RESET'`, the default) or, for a scheduled pipeline's
 *  failed `STAGE:ENRICHMENT` point only, resume on just the missing products
 *  (`mode: 'RESUME_REMAINING'`) — a fresh run of the same `workflowId` that
 *  skips curation and continues to scoring/approval/delivery. Requires the
 *  workflow to be closed (409 while still RUNNING). `runId` defaults to the
 *  latest run when omitted; `reapplySignals` is ignored for `RESUME_REMAINING`. */
export async function rewindEnrichmentWorkflow(
  workflowId: string,
  eventId: number,
  reason: string,
  mode: WorkflowRetryMode,
  runId?: string,
): Promise<WorkflowRewindResponse> {
  return workflowRunsClient
    .post(endpoints.catalogOperationsWorkflow.retry(workflowId), {
      json: {
        ...(runId ? { runId } : {}),
        eventId,
        reason,
        mode,
        ...(mode === 'RESET' ? { reapplySignals: true } : {}),
      },
    })
    .json<WorkflowRewindResponse>();
}

// ---------------------------------------------------------------------------
// Pause / Resume (#6 / #7)
// ---------------------------------------------------------------------------

/** #6 — cooperatively hold the run at the next stage boundary. Returns 501 on
 *  flows that don't implement the pausable mixin; 404 if the run already closed. */
export async function pauseEnrichmentWorkflow(
  workflowId: string,
  body: EnrichmentControlRequest = {},
): Promise<EnrichmentControlResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.pause(workflowId), { json: body })
    .json<EnrichmentControlResponse>();
}

/** #7 — clear the pause and continue. Idempotent (resume on a non-paused run is
 *  a no-op). Same 501/404 semantics as pause. */
export async function resumeEnrichmentWorkflow(
  workflowId: string,
  body: EnrichmentControlRequest = {},
): Promise<EnrichmentControlResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.resume(workflowId), { json: body })
    .json<EnrichmentControlResponse>();
}

// ---------------------------------------------------------------------------
// Restart — hard-restart on remaining products (same id)
// ---------------------------------------------------------------------------

/** Terminate the current parent run and start a fresh one under the same id in
 *  resume mode — only the not-yet-enriched products are processed (curation is
 *  reused). `body` is entirely optional; `{}` restarts with unchanged config.
 *  `400` on a non-positive subBatchSize/batchSize; `404`/`409` if the run isn't
 *  in a resumable state. */
export async function restartEnrichmentWorkflow(
  workflowId: string,
  body: RestartEnrichmentRequest = {},
): Promise<RestartEnrichmentResponse> {
  return workflowRunsClient
    .post(endpoints.workflowRuns.enrichmentRestart(workflowId), { json: body })
    .json<RestartEnrichmentResponse>();
}

/** Fetch the run detail and derive the restart gate (is the run at
 *  ENRICHMENT-or-later, and how many curated products remain). Reuses the
 *  workflow-tracking read endpoint but keeps its own minimal projection so the
 *  feature stays decoupled from that DTO. */
export async function getEnrichmentRunProgress(workflowId: string): Promise<EnrichmentRunProgress> {
  const detail = await workflowRunsClient
    .get(endpoints.workflowRuns.detail(workflowId))
    .json<RestartRunDetail>();
  return deriveRestartProgress(detail);
}

/** Minimal projection of the run-detail payload for the health read — the wire
 *  carries far more; we read only the `health` block. Kept local (not imported
 *  from workflow-tracking) so the feature stays decoupled from that DTO. */
interface RunHealthDetail {
  health?: { paused?: boolean; state?: string; blockedReason?: string | null } | null;
}

/** Read the run's live health — whether an operator pause is in effect. The
 *  funnel's trigger-status poll doesn't carry this, so it comes from the same
 *  run-detail endpoint the console uses (the only surface that exposes `paused`;
 *  a paused run still reports `RUNNING`). Absent fields default to "not paused"
 *  so a bare/older response reads as a normal running run. */
export async function getEnrichmentRunHealth(workflowId: string): Promise<EnrichmentRunHealth> {
  const detail = await workflowRunsClient
    .get(endpoints.workflowRuns.detail(workflowId))
    .json<RunHealthDetail>();
  return {
    paused: detail.health?.paused ?? false,
    state: detail.health?.state ?? 'UNKNOWN',
    blockedReason: detail.health?.blockedReason ?? null,
  };
}
